import { AvatarRegistry } from '../venue/AvatarRegistry';

// avatarPrefetch — warm the browser HTTP cache for a known avatar GLB.
//
// Used by the landing page when it learns who's currently spinning in
// the booth. By the time the visitor clicks "Enter Room", the GLB is
// already sitting in the disk cache, so GLTFLoader's fetch on the room
// page is satisfied without a network round-trip and the DJ avatar
// resolves visibly faster.
//
// Implementation: <link rel="prefetch" as="fetch"> in document.head.
// `prefetch` is the right semantic for a resource we expect to need on
// the next navigation; browsers fetch it at low priority so the
// landing page's own critical resources still win the line. Cross-
// origin: anonymous so the cached entry matches what GLTFLoader
// (THREE's default crossOrigin = 'anonymous') will later fetch — a
// mismatch on credentials mode would force a second uncached request.

const PREFETCH_ATTR = 'data-glb-prefetch';

/** Currently-prefetched def ids — mirrors the <link> tags in the head.
 *  Idempotency guard: calling prefetchAvatarByDefId twice for the same
 *  id is a no-op after the first call. */
const prefetched = new Set<string>();

/** Inject a low-priority prefetch hint for the avatar GLB belonging to
 *  `defId`. Resolves once the hint is in place (or noops if the def
 *  can't be resolved). Safe to call repeatedly with the same id.
 *
 *  No-op when:
 *    - running in a non-browser environment (no document)
 *    - the def doesn't exist in the registry (yet — caller can retry
 *      after AvatarRegistry hydrates)
 *    - the def's geometry path is missing
 *    - this id has already been prefetched in this session */
export async function prefetchAvatarByDefId(defId: string): Promise<void> {
  if (typeof document === 'undefined') return;
  if (prefetched.has(defId)) return;
  // Wait for the registry's first server hydrate to complete so we
  // don't bail on a transient cache miss. If hydration has already
  // resolved (cached page reload), this is effectively synchronous.
  try {
    await AvatarRegistry.ready;
  } catch {
    // Registry hydrate threw — fall through and try the in-memory
    // cache, which may still have an entry from localStorage.
  }
  const def = AvatarRegistry.get(defId);
  const path = def?.geometry?.path;
  if (!path) return;
  // Double-check after the await — a parallel call may have raced us.
  if (prefetched.has(defId)) return;
  prefetched.add(defId);

  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.as = 'fetch';
  link.href = path;
  link.crossOrigin = 'anonymous';
  link.setAttribute(PREFETCH_ATTR, defId);
  document.head.appendChild(link);
}

/** Drop any prefetch hint for `defId` (e.g. avatar was deleted from
 *  the registry, or the room rotated to a different DJ and we want to
 *  stop holding the prior GLB hot). The HTTP cache entry itself stays
 *  — this just removes the DOM hint. */
export function clearPrefetch(defId: string): void {
  if (!prefetched.delete(defId)) return;
  if (typeof document === 'undefined') return;
  const node = document.head.querySelector(`link[${PREFETCH_ATTR}="${CSS.escape(defId)}"]`);
  if (node) node.remove();
}
